import { ErrorCode } from '@utils/errorCodes';

/**
 * GraphQL codes the global errorLink (useApolloClient) already turns into a
 * user-facing message — or a logout — for a mutation. A call site showing its
 * own error on top would give two messages for one failure, and errorLink's is
 * the more specific one. Keep in step with errorLink's branches.
 */
const GLOBALLY_SURFACED_MUTATION_CODES: ReadonlySet<string> = new Set([
  ErrorCode.UNAUTHENTICATED,
  'UNAUTHORIZED',
  ErrorCode.PERMISSION_DENIED,
  ErrorCode.CONFLICT,
  ErrorCode.SUBSCRIPTION_REQUIRED,
  ErrorCode.INTERNAL_ERROR,
  ErrorCode.SERVICE_UNAVAILABLE,
  ErrorCode.RATE_LIMIT_EXCEEDED,
]);

type GraphQLErrorLike = { extensions?: { code?: unknown } };

/**
 * The HTTP statuses errorLink reports on its own: 401 (session → logout),
 * 429 (rate limit) and 5xx other than 503 (server error / Cloudflare edge).
 * A single 503 only counts towards its circuit breaker, and a 4xx or a plain
 * connectivity failure (no status at all) gets no message there.
 */
const isNetworkStatusSurfacedGlobally = (statusCode: unknown): boolean =>
  typeof statusCode === 'number' &&
  (statusCode === 401 ||
    statusCode === 429 ||
    (statusCode >= 500 && statusCode !== 503));

/**
 * Whether a failed completeTask needs the caller's own error message, i.e.
 * errorLink stays silent about it (e.g. TaskUnassigned, a missing completion
 * photo, or no connection at all). Pass the resolved result's `errors` under
 * the client's `errorPolicy: 'all'`, or the rejection for a network failure.
 */
export const completeTaskFailureNeedsMessage = (
  failure: { errors: readonly GraphQLErrorLike[] } | { rejection: unknown }
): boolean => {
  if ('errors' in failure) {
    return !failure.errors.some((e) => {
      const code = e.extensions?.code;
      return (
        typeof code === 'string' && GLOBALLY_SURFACED_MUTATION_CODES.has(code)
      );
    });
  }
  const statusCode = (
    failure.rejection as { networkError?: { statusCode?: unknown } } | null
  )?.networkError?.statusCode;
  return !isNetworkStatusSurfacedGlobally(statusCode);
};
